import React, { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import ApexCharts from "apexcharts";
import RapidTrustDrawer from "./RapidTrustDrawer";
import { getReportList } from "../../individual-results/api/individualResults.api";

interface Props {
  data: number[];
  interactive?: boolean;
}

const labels = [
  "Independence",
  "Advancement",
  "Exploring",
  "Caring",
  "Collaboration",
  "Safety",
];

const RapidTrustRadar: React.FC<Props> = ({ data, interactive = true }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [reportData, setReportData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [image, setImage] = useState<string | null>(null);

  const isPrintMode = !interactive;

  const openDrawer = async (name: string) => {
    if (!interactive) return;

    setDrawerOpen(true);
    setLoading(true);

    try {
      const res = await getReportList({
        page: 1,
        limit: 10,
        search: "Rapid",
      });

      const report = res.data.rows.find(
        (item: any) => item.name === "Rapid Trust Build"
      );

      if (!report) return;

      const parsedValues = JSON.parse(report.values);

      const selectedReport = parsedValues.find(
        (item: any) =>
          item.attributes.title.toLowerCase() === name.toLowerCase()
      );

      setReportData(selectedReport?.attributes || null);
    } catch (err) {
      console.error("Report error:", err);
    } finally {
      setLoading(false);
    }
  };

  const series = [
    {
      name: "Rapid Trust",
      data,
    },
  ];

  const options: any = {
    chart: {
      id: "rapidTrustRadar",
      type: "radar",
      toolbar: { show: false },
    },
    stroke: {
      width: 2,
      colors: ["#5b5fc7"],
    },
    fill: {
      opacity: 0.35,
      colors: ["#5b5fc7"],
    },
    markers: {
      size: 4,
      colors: ["#5b5fc7"],
      strokeColors: "#ffffff",
    },
    xaxis: {
      categories: labels,
      labels: { show: false },
    },
    yaxis: {
      min: 0,
      max: 100,
      show: false,
    },
    tooltip: { enabled: false },
  };

  useEffect(() => {
    if (!isPrintMode) return;

    const generateImage = async () => {
      try {
        const result = await ApexCharts.exec(
          "rapidTrustRadar",
          "dataURI"
        );
        setImage(result.imgURI);
      } catch (err) {
        console.error("Chart image generation failed:", err);
      }
    };

    const timer = setTimeout(generateImage, 1200);
    return () => clearTimeout(timer);
  }, [data, isPrintMode]);

  const labelPositions = [
    "top-[-10px] left-1/2 -translate-x-1/2",
    "top-[20%] right-[-10px]",
    "bottom-[18%] right-[2%]",
    "bottom-[-10px] left-1/2 -translate-x-1/2",
    "bottom-[15%] left-[3%]",
    "top-[20%] left-[-10px]",
  ];

  return (
    <div className="relative w-full max-w-[360px] mx-auto px-4 sm:px-0">

      {/* ✅ Chart (UI) OR Image (PRINT) */}
      {isPrintMode && image ? (
        <img
          src={image}
          alt="Radar Chart"
          className="sm:h-[340px]"
          style={{ width: "100%", height: "auto" }}
        />
      ) : (
        <ReactApexChart
          options={options}
          series={series}
          type="radar"
          height={280}
          className="sm:h-[340px]"
        />
      )}

      {/* ✅ ORIGINAL LABELS (UNCHANGED) */}
      <div className="absolute inset-0">
        {labels.map((label, i) => {
          const mobilePositions = [
            "top-0 left-1/2 -translate-x-1/2",
            "top-[18%] right-0",
            "bottom-[18%] right-[5%]",
            "bottom-0 left-1/2 -translate-x-1/2",
            "bottom-[18%] left-[5%]",
            "top-[18%] left-0",
          ];

          const desktopPositions = labelPositions;

          return (
            <div
              key={label}
              onClick={() => interactive && openDrawer(label)}
              onMouseEnter={() => interactive && setHoverIndex(i)}
              onMouseLeave={() => interactive && setHoverIndex(null)}
              className={`
                absolute
                ${mobilePositions[i]}
                sm:${desktopPositions[i]}
                text-[11px] sm:text-[13px]
                font-semibold
                text-[#3b519c]
                ${interactive ? "cursor-pointer hover:text-[#1f2a68]" : ""}
              `}
            >
              {label}

              {interactive && hoverIndex === i && (
                <div className="absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap bg-[#26295e] text-white text-[10px] sm:text-[11px] px-2 py-[3px] rounded-md shadow-lg">
                  {data[i]}%
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Drawer */}
      {interactive && (
        <RapidTrustDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          loading={loading}
          report={reportData}
        />
      )}
    </div>
  );
};

export default RapidTrustRadar;